import { useOrg } from '@client/hooks/business/org/useOrg';
import { useUser } from '@client/hooks/business/user/useUser';
import { usePageSettled } from '@client/hooks/system/usePageSettled';
import { useFeatureFlag, useFeatureFlagSettlement } from '@client/hooks/useFeatureFlag';
import * as Sentry from '@sentry/react';
import { HELP_CENTRE_V2_FLAG } from '@shared/constants/business/help-centre';
import { useLocation } from '@tanstack/react-router';
import { lazy, Suspense, useState } from 'react';

// `ProductFruitsSetup` statically imports `react-product-fruits`, which
// in turn fetches `productfruits/script.js` + `tls.js` + a translations
// JSON (Sentry saw ~4 s of competing bandwidth on cold loads). Lazy-load
// the component itself so the chunk never enters the entry graph, and
// gate render on `usePageSettled` (post-`window.load` + grace) so the
// SDK boot waits until after LCP. The top-bar launcher already guards
// `window.curiumProductFruitsLauncher`, so a delayed mount is safe.
// Import the file, not the `_core/utils` barrel: the barrel re-exports it statically.
const ProductFruitsSetup = lazy(() => import('./ProductFruitsSetup').then((m) => ({ default: m.ProductFruitsSetup })));

function isProductFruitsEligiblePath(pathname: string): boolean {
    return pathname === '/admin-console' || pathname.startsWith('/admin-console/') || pathname.startsWith('/console/');
}

/**
 * Mounts Product Fruits once the page has settled, unless the help centre v2 flag replaces it
 * (CUR-2690). The flag is evaluated per user and organisation, so the decision waits for that
 * identity's evaluation: nothing renders while it is pending, and the lazy chunk is never
 * requested when the flag is on. A timed-out evaluation behaves as flag off, as before.
 * `ProductFruitsSetup` renders nothing without a signed-in user and organisation either, so
 * waiting for them only defers the chunk request.
 *
 * Once the flag has been evaluated on for an identity, Product Fruits stays off for that identity
 * for the rest of the document. When another tab of the app boots, posthog-js copies that tab's
 * persisted flags into this one through its storage listener, so the flag can read as unset for a
 * moment while readiness still says evaluated; without the latch that moment mounts Product Fruits.
 * Turning the flag off mid-session therefore brings Product Fruits back on the next page load.
 */
export function DeferredProductFruitsSetup() {
    const { pathname } = useLocation();
    const pageSettled = usePageSettled();
    const user = useUser({ throwOnUndefined: false });
    const org = useOrg({ throwOnUndefined: false });
    const settlement = useFeatureFlagSettlement({ userId: user?.id.toString(), orgId: org?.id.toString() });
    const helpCentreV2Enabled = useFeatureFlag(HELP_CENTRE_V2_FLAG, false);
    const identityKey = user && org ? `${user.id}:${org.id}` : null;
    const helpCentreV2EvaluatedOn = settlement === 'evaluated' && helpCentreV2Enabled;
    // The identity the flag has been evaluated on for, kept from earlier renders (see above).
    const [helpCentreV2OnFor, setHelpCentreV2OnFor] = useState<string | null>(null);
    if (helpCentreV2EvaluatedOn && identityKey !== null && helpCentreV2OnFor !== identityKey) {
        setHelpCentreV2OnFor(identityKey);
    }

    if (!pageSettled || !isProductFruitsEligiblePath(pathname)) return null;
    if (!user || !org || settlement === 'pending') return null;
    if (helpCentreV2EvaluatedOn || helpCentreV2OnFor === identityKey) return null;
    // ProductFruits is non-critical onboarding tooling — if the lazy
    // chunk fetch fails (e.g. a stale tab across a deploy where the old
    // chunk hash is no longer served by Cloudflare) we silently drop it
    // rather than crashing the root tree.
    return (
        <Sentry.ErrorBoundary fallback={() => <></>}>
            <Suspense fallback={null}>
                <ProductFruitsSetup />
            </Suspense>
        </Sentry.ErrorBoundary>
    );
}
